// PreviewTester/components/Tour/TourTooltip.js
import React, { useState, useEffect, useRef } from 'react';

const TourTooltip = ({
  isOpen,
  onNext,
  onSkip,
  children,
  title,
  description,
  position = 'bottom',
  targetId,
  offset = 12,
  step,
  totalSteps,
  icon = '💡',
}) => {
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const [tooltipHeight, setTooltipHeight] = useState(0);

  if (!isOpen) return null;

  // Get target element position
  const targetElement = targetId ? document.getElementById(targetId) : null;

  useEffect(() => {
    if (!targetElement) return;

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      let top = rect.top + scrollY;
      let left = rect.left + scrollX + rect.width / 2;

      // Adjust for tooltip height
      if (tooltipRef.current) {
        const height = tooltipRef.current.offsetHeight || 0;
        const width = tooltipRef.current.offsetWidth || 0;

        switch (position) {
          case 'top':
            top = rect.top + scrollY - height - offset;
            left = rect.left + scrollX + rect.width / 2 - width / 2;
            break;
          case 'bottom':
            top = rect.bottom + scrollY + offset;
            left = rect.left + scrollX + rect.width / 2 - width / 2;
            break;
          case 'left':
            top = rect.top + scrollY + rect.height / 2 - height / 2;
            left = rect.left + scrollX - width - offset;
            break;
          case 'right':
            top = rect.top + scrollY + rect.height / 2 - height / 2;
            left = rect.right + scrollX + offset;
            break;
          default:
            top = rect.bottom + scrollY + offset;
            left = rect.left + scrollX + rect.width / 2 - width / 2;
        }
      }

      setCoords({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [targetElement, position, offset]);

  // Highlight the target element
  useEffect(() => {
    if (targetElement && isOpen) {
      targetElement.style.outline = '3px solid #4299e1';
      targetElement.style.outlineOffset = '4px';
      targetElement.style.borderRadius = '4px';
      targetElement.style.boxShadow = '0 0 0 4px rgba(66, 153, 225, 0.2)';
    }

    return () => {
      if (targetElement) {
        targetElement.style.outline = '';
        targetElement.style.outlineOffset = '';
        targetElement.style.borderRadius = '';
        targetElement.style.boxShadow = '';
      }
    };
  }, [targetElement, isOpen]);

  return (
    <>
      {/* Backdrop overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.3)',
        zIndex: 9998,
        pointerEvents: 'none',
      }} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          zIndex: 9999,
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          maxWidth: '360px',
          width: '90%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          border: '2px solid #4299e1',
          transform: 'translateX(-50%)',
          animation: 'slideUp 0.3s ease',
          pointerEvents: 'auto',
        }}
      >
        <style>
          {`
            @keyframes slideUp {
              from { opacity: 0; transform: translateX(-50%) translateY(10px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}
        </style>

        {/* Arrow */}
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: '10px solid #4299e1',
        }} />

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px',
        }}>
          <span style={{ fontSize: '18px' }}>{icon}</span>
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#2d3748',
          }}>
            {title}
          </span>
          <span style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: '#a0aec0',
          }}>
            {step}/{totalSteps}
          </span>
        </div>

        {/* Description */}
        <p style={{
          fontSize: '13px',
          color: '#4a5568',
          lineHeight: '1.5',
          margin: '0 0 12px 0',
        }}>
          {description}
        </p>

        {/* Custom content */}
        {children && (
          <div style={{ marginBottom: '12px' }}>
            {children}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onSkip}
            style={{
              padding: '4px 12px',
              backgroundColor: 'transparent',
              color: '#a0aec0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.color = '#718096'}
            onMouseLeave={(e) => e.target.style.color = '#a0aec0'}
          >
            Skip
          </button>
          <button
            onClick={onNext}
            style={{
              padding: '4px 16px',
              backgroundColor: '#4299e1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#3182ce'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#4299e1'}
          >
            Next →
          </button>
        </div>
      </div>
    </>
  );
};

export default TourTooltip;